<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>

  <header>
    <div class="headline">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew() && productionWorkOrder()) {
        <csl-dropdown [formControl]="status" [enumObject]="statusEnumObject" [enumValueOptions]="availableStatuses()" [disabled]="!canChangeStatus()" />
      }
    </div>
    <div class="buttons">
      <span class="separator"></span>
      @if (canEdit()) {
        <p-button [label]="'Save' | translateWF: 'Save'" icon="pi pi-save" (onClick)="save()" [loading]="isSaving()" />
      }
      <p-button [label]="'productionWorkOrder.back' | translateWF: 'Back'" variant="outlined" (onClick)="back()" />
    </div>
  </header>

  <div class="content">
    <!-- HEADER FORM -->
    <p-panel [header]="'PWO_Details_General' | translateWF: 'General Information'">
      <p-card>
        <div class="grid-auto-2">
          <div class="form-box">
            <label>{{ 'PWO_Details_Estimated_Start' | translateWF: 'Estimated Start Date' }}</label>
            @if (canEdit()) {
              <p-calendar [formControl]="headerForm.controls.estimatedStartDate" dateFormat="dd/mm/yy" styleClass="w-full" [showIcon]="true" />
            } @else {
              <span>{{ productionWorkOrder()?.estimatedStartDate | date: 'dd/MM/yyyy' }}</span>
            }
          </div>
          <div class="form-box">
            <label>{{ 'PWO_Details_Estimated_End' | translateWF: 'Estimated End Date' }}</label>
            @if (canEdit()) {
              <p-calendar [formControl]="headerForm.controls.estimatedEndDate" dateFormat="dd/mm/yy" styleClass="w-full" [showIcon]="true" />
            } @else {
              <span>{{ productionWorkOrder()?.estimatedEndDate | date: 'dd/MM/yyyy' }}</span>
            }
          </div>
        </div>
      </p-card>
    </p-panel>

    <!-- ITEMS -->
    <p-panel [header]="'PWO_Details_Items' | translateWF: 'Items'">
      <p-card data-variant="compact">
        <!-- Recipe search bar (add items from here) -->
        @if (canEdit()) {
          <div class="add-item-bar">
            <label class="text-sm text-gray-500 mb-1 block">{{ 'productionWorkOrder.searchAndAddRecipe' | translateWF: 'Search and add a product recipe' }}</label>
            <p-autoComplete
              [ngModel]="selectedRecipe()"
              (ngModelChange)="selectedRecipe.set($event)"
              #recipeSearch="cslServerSearch"
              [cslServerSearch]="fetchRecipes"
              [suggestions]="recipeSearch.results()"
              (onSelect)="onRecipeSelected($event)"
              field="name"
              [placeholder]="'productionWorkOrder.typeToSearchRecipes' | translateWF: 'Type to search recipes...'"
              styleClass="w-full"
              appendTo="body"
            >
              <ng-template let-recipe pTemplate="item">
                <div class="flex items-center gap-2">
                  <span class="font-medium">{{ recipe.number }}</span>
                  <span class="text-gray-500">—</span>
                  <span>{{ recipe.name }}</span>
                </div>
              </ng-template>
            </p-autoComplete>
          </div>
        }

        <!-- Items table -->
        @if (itemsFormArray.length > 0) {
          <p-table [value]="itemsFormArray.controls" styleClass="p-datatable-sm mt-3">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 30%">{{ 'PWO_Details_Recipe' | translateWF: 'Recipe' }}</th>
                <th style="width: 25%">{{ 'PWO_Details_Recipe_Name' | translateWF: 'Name' }}</th>
                <th style="width: 10%">{{ 'PWO_Details_UOM' | translateWF: 'UOM' }}</th>
                <th style="width: 15%">{{ 'PWO_Details_Quantity' | translateWF: 'Quantity' }} *</th>
                <th style="width: 12%">{{ 'PWO_Details_Reservations' | translateWF: 'Reservations' }}</th>
                @if (canEdit()) {
                  <th style="width: 8%"></th>
                }
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-itemGroup let-i="rowIndex">
              <tr [formGroup]="itemGroup">
                <td class="font-medium">{{ itemGroup.getRawValue().productRecipeNumber }}</td>
                <td class="text-gray-600">{{ itemGroup.getRawValue().recipeName }}</td>
                <td>
                  <span class="text-sm text-gray-500">{{ getUomLabel(itemGroup.getRawValue().uom) }}</span>
                </td>
                <td>
                  @if (canEdit()) {
                    <p-inputNumber formControlName="quantity" [min]="0.01" mode="decimal" [minFractionDigits]="2" styleClass="w-full" placeholder="0.00" />
                  } @else {
                    {{ itemGroup.getRawValue().quantity }}
                  }
                </td>
                <td>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-500">{{ getReservations(i).length }}</span>
                    @if (canEdit()) {
                      <p-autoComplete
                        #clientPoSearch="cslServerSearch"
                        [cslServerSearch]="fetchClientPOs(i)"
                        [suggestions]="clientPoSearch.results()"
                        (onSelect)="onClientPOSelect($event, i)"
                        field="number"
                        [placeholder]="'productionWorkOrder.addClientPo' | translateWF: 'Add CPO...'"
                        styleClass="reservation-search"
                        appendTo="body"
                        [forceSelection]="true"
                      >
                        <ng-template let-po pTemplate="item">
                          <div class="text-sm">
                            <strong>{{ po.number }}</strong>
                            @if (po.client?.name) {
                              <span class="text-gray-500"> — {{ po.client?.name }}</span>
                            }
                          </div>
                        </ng-template>
                      </p-autoComplete>
                    }
                  </div>
                </td>
                @if (canEdit()) {
                  <td class="text-center">
                    <p-button icon="pi pi-trash" severity="danger" [text]="true" size="small" (onClick)="removeItem(i)" />
                  </td>
                }
              </tr>

              <!-- Expanded reservations -->
              @if (getReservations(i).length > 0) {
                <tr class="reservation-row">
                  <td [attr.colspan]="canEdit() ? 6 : 5" class="p-0">
                    <div class="reservation-section">
                      <table class="reservation-table">
                        <thead>
                          <tr>
                            <th>{{ 'productionWorkOrder.clientPo' | translateWF: 'Client PO' }}</th>
                            <th>{{ 'productionWorkOrder.client' | translateWF: 'Client' }}</th>
                            <th class="text-right">{{ 'productionWorkOrder.reservedQty' | translateWF: 'Reserved Qty' }}</th>
                            @if (canEdit()) {
                              <th></th>
                            }
                          </tr>
                        </thead>
                        <tbody>
                          @for (resGroup of getReservations(i).controls; track resGroup; let j = $index) {
                            <tr [formGroup]="resGroup">
                              <td>
                                <span>{{ resGroup.value.clientPurchaseOrderNumber }}</span>
                                @if (hasReservationMismatch(i, j)) {
                                  <small class="p-error mt-1 block">
                                    <i class="pi pi-exclamation-triangle mr-1"></i>
                                    {{ 'productionWorkOrder.noMatchingRecipeInPo' | translateWF: 'No matching recipe found in this PO' }}
                                  </small>
                                }
                              </td>
                              <td>
                                <span class="text-sm text-gray-600">{{ resGroup.value.clientName ?? '—' }}</span>
                              </td>
                              <td>
                                @if (canEdit()) {
                                  <p-inputNumber formControlName="reservedQuantity" [min]="0.01" mode="decimal" [minFractionDigits]="2" styleClass="w-full" placeholder="0.00" />
                                } @else {
                                  {{ resGroup.value.reservedQuantity }}
                                }
                              </td>
                              @if (canEdit()) {
                                <td class="text-center">
                                  <p-button icon="pi pi-trash" severity="danger" [text]="true" size="small" (onClick)="removeReservation(i, j)" />
                                </td>
                              }
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              }
            </ng-template>
          </p-table>
        } @else {
          <div class="empty-items">
            <i class="pi pi-inbox text-3xl text-gray-300 mb-2"></i>
            <p class="text-gray-400 text-sm">{{ 'productionWorkOrder.noItemsYet' | translateWF: 'No items added yet. Search for a recipe above to get started.' }}</p>
          </div>
        }
      </p-card>
    </p-panel>

    <!-- BATCHES -->
    @if (!isNew()) {
      <p-panel [header]="'PWO_Details_Batches' | translateWF: 'Batches'">
        <ng-template pTemplate="icons">
          @if (canCreateBatch()) {
            <p-button [label]="'productionWorkOrder.newBatch' | translateWF: 'New Batch'" icon="pi pi-plus" size="small" (onClick)="navigateToCreateBatch()" />
          }
        </ng-template>

        <p-card data-variant="compact">
          @if (relatedBatches().length > 0) {
            <p-table [value]="relatedBatches()" styleClass="p-datatable-sm">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'productionWorkOrder.number' | translateWF: 'Number' }}</th>
                  <th>{{ 'productionWorkOrder.status' | translateWF: 'Status' }}</th>
                  <th>{{ 'productionWorkOrder.productRecipe' | translateWF: 'Product Recipe' }}</th>
                  <th class="text-right">{{ 'productionWorkOrder.plannedQty' | translateWF: 'Planned Qty' }}</th>
                  <th class="text-right">{{ 'productionWorkOrder.actualQty' | translateWF: 'Actual Qty' }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-batch>
                <tr class="cursor-pointer" (click)="navigateToBatch(batch.id)">
                  <td class="font-medium">{{ batch.number }}</td>
                  <td>
                    <p-tag [value]="getBatchStatusLabel(batch.status)" [severity]="getBatchStatusSeverity(batch.status)" />
                  </td>
                  <td>
                    <span class="font-medium">{{ batch.productDevelopmentNumber }}</span>
                    @if (batch.productRecipe?.name) {
                      <span class="text-gray-500 ml-2">{{ batch.productRecipe.name }}</span>
                    }
                  </td>
                  <td class="text-right">{{ batch.plannedQuantity }}</td>
                  <td class="text-right">{{ batch.actualQuantity ?? '—' }}</td>
                </tr>
              </ng-template>
            </p-table>
          } @else {
            <p class="text-gray-400 text-sm text-center py-4">{{ 'productionWorkOrder.noBatchesYet' | translateWF: 'No batches created yet' }}</p>
          }
        </p-card>
      </p-panel>
    }
  </div>
</main>
